import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import NotificationComposer from "@/components/NotificationComposer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Bell, Send } from "lucide-react";

const AUDIENCE_LABELS = { all: "Everyone", team: "Their team", individual: "One employee" };

function when(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Notifications() {
  const [composing, setComposing] = useState(false);

  const { data: sent, isLoading } = useQuery({
    queryKey: ["sentNotifications"],
    queryFn: async () => (await api.get("/notifications/sent")).data,
  });

  if (isLoading) return <Loading />;

  const rows = sent || [];

  return (
    <div>
      <PageHeader
        overline="Communications" title="Notifications" icon={Bell}
        subtitle="Send an announcement to every employee, or a reminder to one person. They see it in their portal."
        actions={
          <Button className="bg-navy hover:bg-navy-700 btn-lift" onClick={() => setComposing(true)}
            data-testid="send-notification-btn">
            <Send className="h-4 w-4 mr-1" /> Send Notification
          </Button>
        }
      />

      {rows.length === 0 ? (
        <EmptyState icon={Bell} title="Nothing sent yet"
          hint="Anything you send appears here with how many people have read it."
          action={
            <Button className="bg-navy hover:bg-navy-700" onClick={() => setComposing(true)}>
              <Send className="h-4 w-4 mr-1" /> Send the first one
            </Button>
          } />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="overline text-slate-500">Sent</TableHead>
                <TableHead className="overline text-slate-500">Title</TableHead>
                <TableHead className="overline text-slate-500">Category</TableHead>
                <TableHead className="overline text-slate-500">Audience</TableHead>
                <TableHead className="overline text-slate-500 text-right">Read</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.batch_id} className="table-row-i" data-testid={`sent-${r.batch_id}`}>
                    <TableCell className="text-slate-500 text-xs whitespace-nowrap">{when(r.created_at)}</TableCell>
                    <TableCell className="font-medium text-navy max-w-[280px]">
                      <p className="truncate">{r.title}</p>
                      <p className="text-slate-400 text-xs font-normal truncate">{r.message}</p>
                    </TableCell>
                    <TableCell className="text-slate-600">{r.category}</TableCell>
                    <TableCell className="text-slate-600">{AUDIENCE_LABELS[r.audience] || r.audience}</TableCell>
                    <TableCell className="text-right text-slate-600 whitespace-nowrap">
                      {r.read} / {r.recipients}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <NotificationComposer mode="admin" open={composing} onOpenChange={setComposing} />
    </div>
  );
}
